import { useEffect, useState } from "react"
import { Button, Checkbox, Notice, Text } from "@smooth/design-system-components"
import { useEvaluationSandbox } from "../useEvaluationSandbox"
import { isArchived } from "../types"
import type { CaseSet } from "../types"

// プロンプトの版を保存したら、回帰テスト（case_set core）でまとめて生成を自動で始める（プロンプトの比較）。有効化（本番への反映）とは独立。
// 入り切りは担当者ごとの好みなので、このブラウザに覚えておく（既定は切）

/** 自動で流す箱。保存時の自動評価は回帰テスト（core）だけ */
export const AUTO_EVALUATE_CASE_SET: CaseSet = "core"
/** 1件あたりの費用の目安（Vertex） */
export const COST_PER_CASE_USD = 0.017
const STORAGE_KEY = "dunning-prompt-auto-evaluate"

export const estimatedCost = (count: number) => `$${(count * COST_PER_CASE_USD).toFixed(2)}`

/** 保存 API が返す、自動のプロンプトの比較の結果（始めなかったときは skippedReason） */
export type AutoEvaluation = {
  batchId: number | null
  totalCount: number | null
  caseSet: string
  skippedReason: string | null
}

export function useAutoEvaluateOnSave() {
  const [enabled, setEnabledState] = useState(false)
  useEffect(() => {
    try {
      setEnabledState(window.localStorage.getItem(STORAGE_KEY) === "true")
    } catch {
      // localStorage が使えない環境では切のまま始める
    }
  }, [])
  const setEnabled = (value: boolean) => {
    setEnabledState(value)
    try {
      window.localStorage.setItem(STORAGE_KEY, String(value))
    } catch {
      // 覚えられなくても、この画面の間は切り替わる
    }
  }
  const { library } = useEvaluationSandbox()
  const count = library.data?.data.filter(
    (record) => record.caseSet === AUTO_EVALUATE_CASE_SET && !isArchived(record)
  ).length
  return {
    /** 検証を止めている（一覧を取れない）ときは出さない */
    available: !library.error && count !== undefined,
    enabled,
    setEnabled,
    count: count ?? 0,
    /** 保存 API に渡す箱（切なら undefined） */
    caseSet: enabled && count ? AUTO_EVALUATE_CASE_SET : undefined,
  }
}

export type AutoEvaluateState = ReturnType<typeof useAutoEvaluateOnSave>

/** 保存ボタンの近くに置く、自動で流すかの切り替え */
export function AutoEvaluateOption({
  state,
  disabled,
}: {
  state: AutoEvaluateState
  disabled?: boolean
}) {
  if (!state.available) return null
  return (
    <div data-smooth-ds className="flex flex-col gap-1">
      <Checkbox
        label={`保存したら回帰テスト（${state.count}件）で比較を実行する`}
        checked={state.enabled}
        disabled={disabled || state.count === 0}
        onChange={(e) => state.setEnabled(e.target.checked)}
      />
    </div>
  )
}

/** 保存したあとの知らせ。始めたプロンプトの比較を開くか、始めなかった理由を出す */
export function AutoEvaluateResult({
  result,
  onOpen,
  onClose,
}: {
  result: AutoEvaluation
  onOpen?: (batchId: number) => void
  onClose: () => void
}) {
  const batchId = result.batchId
  return (
    <div data-smooth-ds role="status" aria-label="保存時のプロンプトの比較">
      <Notice tone={batchId ? "info" : "warning"} variant="card">
        <div className="flex flex-wrap items-center gap-3">
          <Text>
            {batchId
              ? `保存した版で、プロンプトの比較 #${batchId}（${result.totalCount ?? 0}件）を始めました。`
              : `自動のプロンプトの比較は始めませんでした：${result.skippedReason ?? "理由不明"}`}
          </Text>
          {batchId && onOpen && (
            <Button onClick={() => onOpen(batchId)}>プロンプトの比較 #{batchId} を開く</Button>
          )}
          <Button variant="outline" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </Notice>
    </div>
  )
}
